import React, { useState, useEffect, useRef } from 'react';
import {
  Layout,
  Table,
  Input,
  Tooltip,
  Typography,
  Select,
  InputNumber,
  message,
} from 'antd';
import { usePageTitle } from 'hooks';
import LoadingSpinner from 'utils/sharedComponents/LoadingSpinner';
import { DynamicPricingServices } from 'services/dynamicPricingServices/dynamicPricing.service';
import { IRuleDatum, IUserDynamicPricingData } from 'models/interfaces/Iglobal';
import { ICustomError } from 'models/interfaces/ICustomError';
import { LoggerService } from 'sharedServices';
import CustomError from 'controls/CustomError/CustomError';
import { useRbac } from 'features/rbac/rbac';
import { ERbacPermissions } from 'features/rbac/rbacPermissionsList';
import FilterDrawer from 'utils/commonFilter/FilterDrawer';
import { useDispatch } from 'react-redux';
import { actions, brokerUsers, listerUsers } from 'models/Slices/FilterSlice';
import { dataSource } from 'models/Slices/GlobalSlice';
import { useAppSelector } from 'app/hooks';

const delistingThresholdVal: number = Number(
  process.env.REACT_APP_DELISTING_THRESHOLD ?? 5,
);

const baseData: IRuleDatum[] = [
    {
      id: -3,
      within_day: '61 - 90',
      adjust_by: '%',
      amount: 0,
      every_days: 0,
      direction: '0',
      isSaved: true,
      isEditing: false,
    },
    {
      id: -2,
      within_day: '31 - 60',
      adjust_by: '%',
      amount: 0,
      every_days: 0,
      direction: '0',
      isSaved: true,
      isEditing: false,
    },

    {
      id: -1,
      within_day: process.env.REACT_APP_DELISTING_THRESHOLD + ' - 30',
      adjust_by: '%',
      amount: 0,
      every_days: 0,
      direction: '0',
      isSaved: true,
      isEditing: false,
    },
  ],
  nonEditableStyle = {
    border: 'none',
    background: 'transparent',
    cursor: 'not-allowed',
    color: 'rgba(0, 0, 0, 0.85)',
  };

const Rules: React.FC = () => {
  usePageTitle('Dynamic Pricing');

  const { Option } = Select;
  const dispatch = useDispatch();
  const brokers = JSON.stringify(useAppSelector(brokerUsers));
  const listers = JSON.stringify(useAppSelector(listerUsers));
  const DataSource = useAppSelector(dataSource);
  const [ruleMetaId, setRuleMetaId] = useState<number>(0),
    [newBaseData, setNewBaseData] = useState<IRuleDatum[] | null>(null),
    [defaultMinMarkup, setDefaultMinMarkup] = useState<number>(10),
    [ruleData, setRuleData] = useState<IRuleDatum[]>(baseData),
    [previousData, setPreviousData] = useState<IRuleDatum[]>(baseData),
    [showLoader, setShowLoader] = useState<boolean>(true),
    [previousMinMarkup, setPreviousMinMarkup] = useState<number>(0),
    [minMarkup, setMinMarkup] = useState<number>(0),
    [intermediateMinMarkup, setIntermediateMinMarkup] = useState<number>(0),
    [isSaveDisabledRules, setIsSaveDisabledRules] = useState<boolean>(false),
    [isSaveDisabledMarkup, setIsSaveDisabledMarkup] = useState<boolean>(false),
    [dataFetchError, setDataFetchError] = useState<ICustomError | null>(null),
    [isDisableEditing, setIsDisableEditing] = useState<boolean>(false),
    [isDisableReset, setIsDisableReset] = useState<boolean>(true),
    [errors, setErrors] = useState<{
      [id: number]: { [field: string]: string };
    }>({}),
    [ifShowNoDataMsg, setIfShowNoDataMsg] = useState<boolean>(false),
    [filterQueryObject, setFilterQueryObject] = useState<{
      broker_user_Id: string | null;
      lister_user_Id: string | null;
    }>({
      broker_user_Id: null,
      lister_user_Id: null,
    }),
    [intermediateRuleRowData, setIntermediateRuleRowData] =
      useState<IRuleDatum | null>(null);

  const { hasPermissions } = useRbac();

  useEffect(() => {
    setShowLoader(true);
    if (hasPermissions([ERbacPermissions.FILTER_BROKER_TAGS_VIEW_ALL]))
      dispatch(actions.getBrokerUserData({ env: DataSource }));

    if (
      hasPermissions([
        ERbacPermissions.FILTER_LISTER_USER_SELECTION_ALL,
        ERbacPermissions.FILTER_LISTER_USER_SELECTION_TEAM,
      ])
    )
      dispatch(actions.getListerUserData({ env: DataSource }));
  }, []);

  useEffect(() => {
    const brokerList = JSON.parse(brokers);
    const listerList = JSON.parse(listers);
    if (listerList.length > 0) {
      let firstBrokerId = brokerList[0]?.value;
      let firstListerId =
        brokerList.length > 0
          ? listerList.filter(
              (lister: any) => lister.broker_user_id === brokerList[0].value,
            )[0]?.value
          : listerList[0]?.value;
      setFilterQueryObject({
        broker_user_Id: firstBrokerId,
        lister_user_Id: firstListerId,
      });
    } else {
      setShowLoader(false);
    }
  }, [brokers, listers]);

  useEffect(() => {
    setIsSaveDisabledMarkup(previousMinMarkup !== minMarkup);
  }, [minMarkup]);

  useEffect(() => {
    setIsSaveDisabledMarkup(
      previousMinMarkup !== intermediateMinMarkup &&
        intermediateMinMarkup >= 10 &&
        intermediateMinMarkup <= 90,
    );
  }, [intermediateMinMarkup]);

  const getUserDynamicPricing = async (lister_id?: string) => {
    try {
      setShowLoader(true);
      setIfShowNoDataMsg(false);
      let newDefault: IRuleDatum[] = newBaseData ?? [],
        newDefaultMarkup = defaultMinMarkup;

      const userDataPromise =
        new DynamicPricingServices().getUserDynamicPricing(lister_id);

      if (
        !newBaseData &&
        hasPermissions([ERbacPermissions.DYNAMIC_PRICING_RULES_ACTIONS])
      ) {
        const defaultRulesPromise =
            new DynamicPricingServices().getDefaultDynamicPricingRules(),
          defaultRulesResponse = await defaultRulesPromise,
          defaultRulesData: any = defaultRulesResponse?.data?.data;

        newDefault = formatData(
          defaultRulesData[0]?.rules,
          defaultRulesData[0]?.rules,
          true,
        );
        newDefaultMarkup = defaultRulesData[0]?.min_markup || 10;
        setDefaultMinMarkup(defaultRulesData[0]?.min_markup || 0);
        setNewBaseData(newDefault);
      }

      const userDataResponse = await userDataPromise;
      const userData: any = userDataResponse?.data?.data;

      if (
        !hasPermissions([ERbacPermissions.DYNAMIC_PRICING_RULES_ACTIONS]) &&
        !userData?.length
      ) {
        setIfShowNoDataMsg(true);
        setShowLoader(false);
        return;
      }

      if (!userData?.length) {
        setPreviousData(newDefault);
        setRuleData(newDefault);
        setPreviousMinMarkup(userData[0]?.min_markup || newDefaultMarkup);
        setMinMarkup(userData[0]?.min_markup || newDefaultMarkup);
        setIntermediateMinMarkup(userData[0]?.min_markup || newDefaultMarkup);
        setIsSaveDisabledMarkup(true);
        setIsSaveDisabledRules(true);
        setShowLoader(false);
        return;
      }

      const formattedData: IRuleDatum[] = formatData(
        userData[0].rules,
        newDefault,
      );

      setIsDisableReset(false);
      setRuleMetaId(userData[0]?.rule_meta_id || 0);
      setPreviousData(formattedData);
      setRuleData(formattedData);
      setPreviousMinMarkup(userData[0]?.min_markup || defaultMinMarkup || 0);
      setMinMarkup(userData[0].min_markup || defaultMinMarkup || 0);
      setIntermediateMinMarkup(
        userData[0]?.min_markup || defaultMinMarkup || 0,
      );
      setShowLoader(false);
    } catch (error: any) {
      setShowLoader(false);
      await new LoggerService().log({
        payload: error,
        function_name: 'getUserDynamicPricing',
      });

      setDataFetchError({ ...error, refreshAction: getUserDynamicPricing });
    }
  };

  const formatData = (
    data: IRuleDatum[],
    newDefaultData: IRuleDatum[] = baseData,
    isFormattingBase: boolean = false,
  ) => {
    if (!data || data.length === 0) return newDefaultData;

    const formatted = baseData.map((base) => {
      const match = data.find((d) => d.within_day === base.within_day);

      return match ? { ...match, isSaved: true, isEditing: false } : base;
    });

    return formatted;
  };

  const applyDefault = () => {
    if (newBaseData) {
      const temp = newBaseData.map((d) => ({
        ...d,
        id: ruleData.find((rd) => rd.within_day === d.within_day)?.id || d.id,
        isSaved: true,
        isEditing: false,
      }));
      setRuleData(temp);
      setMinMarkup(defaultMinMarkup);
      setIntermediateMinMarkup(defaultMinMarkup);

      const formattedPrevRuleData = formatData(
        previousData.map((row) => ({ ...row, isEditing: false })),
      );

      setIsSaveDisabledMarkup(previousMinMarkup !== minMarkup);
      setIsSaveDisabledRules(
        JSON.stringify(temp) !== JSON.stringify(formattedPrevRuleData),
      );
    }
  };

  useEffect(() => {
    filterQueryObject?.lister_user_Id &&
      getUserDynamicPricing(filterQueryObject.lister_user_Id);
  }, [filterQueryObject.lister_user_Id]);

  useEffect(() => {
    if (hasPermissions([ERbacPermissions.DYNAMIC_PRICING_RULES_ACTIONS])) {
      getUserDynamicPricing();
    }
  }, []);

  const [isFloating, setIsFloating] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const checkIfShouldFloat = () => {
    const contentEl = contentRef.current;
    if (!contentEl) return;

    const contentHeight = contentEl.offsetHeight;
    const windowHeight = window.innerHeight;

    setIsFloating(contentHeight > windowHeight);
  };

  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      checkIfShouldFloat();
    });

    window.addEventListener('resize', checkIfShouldFloat);

    return () => {
      window.removeEventListener('resize', checkIfShouldFloat);
      cancelAnimationFrame(raf);
    };
  }, []);

  // Validation function for empty fields
  const validateField = (field: string, value: string) => {
    if (!value || (typeof value === 'string' && value.trim() === '')) {
      return 'This field is required';
    }
    return '';
  };

  // Validate all required fields for a row, returns errors object for that row
  const validateRow = (row: any) => {
    const fieldsToValidate = ['within_day', 'every_days', 'direction'];
    const rowErrors: { [field: string]: string } = {};
    fieldsToValidate.forEach((field) => {
      const error = validateField(field, row[field]);
      if (error) {
        rowErrors[field] = error;
      }
    });
    return rowErrors;
  };

  // Handle change to editable fields only
  const handleInputChange = (id: number, field: string, value: string) => {
    setRuleData((prev) =>
      prev.map((row) => {
        if (row.id !== id) return row;

        let newValue = value;
        if (field === 'amount') {
          const num = Number(value);
          newValue = isNaN(Math.min(Math.max(num, 0), 100))
            ? '0'
            : Math.min(Math.max(num, 0), 100).toString();
          if (!(Number(value) > 0 && Number(value) < 100)) {
            setErrors((prev) => ({
              ...prev,
              [id]: {
                ...prev[id],
                [field]: 'amount does not follows the limit',
              },
            }));
          }

          return { ...row, [field]: Number(newValue) };
        }

        if (field === 'every_days') {
          const num = Number(value);
          newValue = isNaN(Math.min(Math.max(num, 0), 30))
            ? '0'
            : Math.min(Math.max(num, 0), 30).toString();
          if (!(Number(value) > 0 && Number(value) < 30)) {
            setErrors((prev) => ({
              ...prev,
              [id]: {
                ...prev[id],
                [field]: 'every_days does not follows the limit',
              },
            }));
          }

          return { ...row, [field]: Number(newValue) };
        }

        return { ...row, [field]: newValue };
      }),
    );

    // Validate the field and update errors
    const errorMsg = validateField(field, value);
    if (errorMsg.length > 0) {
      setErrors((prev) => ({
        ...prev,
        [id]: {
          ...prev[id],
          [field]: errorMsg,
        },
      }));
    } else {
      setErrors((prev) => {
        const copy = { ...prev };
        delete copy[id];
        return copy;
      });
    }
  };

  const saveInitial = (id: number) => {
    const row = ruleData.find((r) => r.id === id);
    if (row) {
      // Validate all fields on initial save
      const rowErrors = validateRow(row);
      if (Object.values(rowErrors).some((msg) => msg !== '')) {
        setErrors((prev) => ({ ...prev, [id]: rowErrors }));
        return; // Prevent save if errors
      }

      // updateOriginalRow(id, row);

      // Clear errors for this row on successful save
      setErrors((prev) => {
        const copy = { ...prev };
        delete copy[id];
        return copy;
      });

      setRuleData((prev) =>
        prev.map((row) =>
          row.id === id ? { ...row, isSaved: true, isEditing: false } : row,
        ),
      );
    }
  };

  const startEditing = (id: number) => {
    setIntermediateRuleRowData(ruleData.find((r) => r.id === id) || null);
    setIsDisableEditing(true);
    setIsSaveDisabledMarkup(false);
    setIsSaveDisabledRules(false);
    setRuleData((prev) =>
      prev.map((row) => (row.id === id ? { ...row, isEditing: true } : row)),
    );
  };

  const saveEdit = (id: number) => {
    const row = ruleData.find((r) => r.id === id);
    if (!row) return;

    // Validate all required fields before saving
    const rowErrors = validateRow(row);
    if (Object.values(rowErrors).some((msg) => msg !== '')) {
      setErrors((prev) => ({ ...prev, [id]: rowErrors }));
      return; // Prevent save if errors
    }

    // Clear errors on successful save
    setErrors((prev) => {
      const copy = { ...prev };
      delete copy[id];
      return copy;
    });

    const formattedRuleData = formatData(
        ruleData.map((row) =>
          row.id === id ? { ...row, isEditing: false } : row,
        ),
      ),
      formattedPrevRuleData = formatData(
        previousData.map((row) =>
          row.id === id ? { ...row, isEditing: false } : row,
        ),
      );

    setRuleData((prev) =>
      prev.map((row) => (row.id === id ? { ...row, isEditing: false } : row)),
    );

    setIsSaveDisabledMarkup(previousMinMarkup !== minMarkup);
    setIsSaveDisabledRules(
      JSON.stringify(formattedRuleData) !==
        JSON.stringify(formattedPrevRuleData),
    );
    setIsDisableEditing(false);
  };

  const cancelEdit = (id: number) => {
    const original = previousData?.find((r: IRuleDatum) => r.id === id);
    if (!original) return;

    const formattedRuleData = formatData(
        ruleData.map((row) =>
          row.id === id
            ? { ...(intermediateRuleRowData || original), isEditing: false }
            : row,
        ),
      ),
      formattedPrevRuleData = formatData(
        previousData.map((row) =>
          row.id === id ? { ...row, isEditing: false } : row,
        ),
      );

    setRuleData((prev) =>
      prev.map((row) =>
        row.id === id
          ? { ...(intermediateRuleRowData || original), isEditing: false }
          : row,
      ),
    );

    setIsSaveDisabledMarkup(previousMinMarkup !== minMarkup);
    setIsSaveDisabledRules(
      JSON.stringify(formattedRuleData) !==
        JSON.stringify(formattedPrevRuleData),
    );
    setIsDisableEditing(false);
    setIntermediateRuleRowData(null);

    // Clear errors on cancel
    setErrors((prev) => {
      const updated = { ...prev };
      delete updated[id];
      return updated;
    });
  };

  const filterChanged = (filters: any) => {
    setFilterQueryObject({
      lister_user_Id: filters.lister_user_Id,
      broker_user_Id: filters.broker_user_Id,
    });
  };

  const updateMinMarkup = (e: any) => {
    const value = Number(e.target.value);

    switch (true) {
      case isNaN(value) || value < 10:
        setMinMarkup(10);
        setIntermediateMinMarkup(10);
        break;
      case value > 90:
        setMinMarkup(90);
        setIntermediateMinMarkup(90);
        break;
      default:
        setMinMarkup(value);
        setIntermediateMinMarkup(value);
        break;
    }
  };

  const saveOrModifyUserDynamicPricing = async (
    body: IUserDynamicPricingData,
  ) => {
    try {
      setShowLoader(true);
      setIsDisableReset(false);

      const resp =
        await new DynamicPricingServices().addOrModifyDynamicPricingRules(body);

      if (resp?.data?.statusCode !== 200) {
        throw new Error(
          `Failed to save dynamic pricing rules (status: ${
            resp?.data?.statusCode || 500
          })`,
        );
      } else {
        setPreviousData(ruleData);
        setPreviousMinMarkup(minMarkup);
        setIsSaveDisabledRules(false);
        setIsSaveDisabledMarkup(false);
      }
      message.success({
        content: 'Dynamic pricing has been saved successfully',
        key: 'dynamic pricing',
        duration: 5,
        className: 'toastMsg savedSuccess',
      });

      setShowLoader(false);
    } catch (error: any) {
      message.error({
        content: 'Failed to save dynamic pricing',
        duration: 5,
        key: 'dynamic pricing',
        className: 'toastMsg savedFailed',
      });
      setShowLoader(false);
      await new LoggerService().log({
        payload: error,
        function_name: 'saveOrModifyUserDynamicPricing',
      });

      setDataFetchError({
        ...error,
        refreshAction: saveOrModifyUserDynamicPricing,
      });
    }
  };

  const initiateSave = async () => {
    const body: IUserDynamicPricingData = {
      rule_meta_id: ruleMetaId > 0 ? ruleMetaId : 0,
      min_markup: minMarkup,
      if_stop_at_min_markeup: true,
      rules: ruleData.map((r) => ({ ...r, id: r.id > 0 ? r.id : 0 })),
    };

    await saveOrModifyUserDynamicPricing(body);
  };

  const resetData = () => {
    setIsSaveDisabledRules(false);
    setIsSaveDisabledMarkup(false);
    setMinMarkup(previousMinMarkup);
    setIntermediateMinMarkup(previousMinMarkup);
    setRuleData(previousData);
  };

  const columns = [
    {
      title: 'Within (days)',
      key: 'within_day',
      render: (_: any, record: any) => {
        return (
          <Input
            value={record.within_day}
            disabled={true}
            style={nonEditableStyle}
          />
        );
      },
    },
    {
      title: 'Adjust by',
      key: 'adjust_by',
      render: (_: any, record: any) => {
        const isPercentage =
          record.adjust_by === 1 ||
          record.adjust_by === '%' ||
          !record.adjust_by;

        const toggleAdjustBy = () => {
          const newType = isPercentage ? '$' : '%';
          handleInputChange(record.id, 'adjust_by', newType);
        };

        const errorMsg = errors[record.id]?.amount;
        const isEditable =
          record.isEditing &&
          hasPermissions([ERbacPermissions.DYNAMIC_PRICING_RULES_ACTIONS]);

        const formattedValue = isPercentage
          ? `${record.amount ?? ''} %`
          : `$ ${record.amount ?? ''}`;

        return (
          <div className="groupAddon">
            {isEditable && (
              <span className="addon" onClick={toggleAdjustBy}>
                {isPercentage ? '%' : '$'}
              </span>
            )}

            {isEditable ? (
              <InputNumber
                value={record.amount}
                onChange={(value) =>
                  handleInputChange(record.id, 'amount', value)
                }
                className={`adjust-input ${!isEditable ? 'disabled' : ''} ${
                  errorMsg ? 'error' : ''
                }`}
                style={{
                  ...(!record.isEditing ||
                  !hasPermissions([
                    ERbacPermissions.DYNAMIC_PRICING_RULES_ACTIONS,
                  ])
                    ? nonEditableStyle
                    : {}),
                  borderColor: errorMsg ? 'red' : undefined,
                }}
                disabled={!isEditable}
              />
            ) : (
              <span className="non-editable-value">{formattedValue}</span>
            )}
          </div>
        );
      },
    },
    {
      title: 'Every (days)',
      key: 'every_days',
      render: (_: any, record: any) => {
        const errorMsg = errors[record.id]?.every_days;
        return (
          <Input
            value={record.every_days}
            disabled={!record.isEditing}
            onChange={(e) =>
              handleInputChange(record.id, 'every_days', e.target.value)
            }
            style={{
              ...(!record.isEditing ||
              !hasPermissions([ERbacPermissions.DYNAMIC_PRICING_RULES_ACTIONS])
                ? nonEditableStyle
                : {}),
              borderColor: errorMsg ? 'red' : undefined,
            }}
          />
        );
      },
    },
    {
      title: 'Direction',
      key: 'direction',
      render: (_: any, record: any) => {
        // const errorMsg = errors[record.id]?.direction;
        return (
          <Select
            value={record.direction || '0'}
            onChange={(value) =>
              handleInputChange(record.id, 'direction', value)
            }
            disabled={true}
            style={nonEditableStyle}
          >
            <Option value="0">Decrease</Option>
            <Option value="1">Increase</Option>
          </Select>
        );
      },
    },
    {
      title: '',
      dataIndex: 'id',
      key: 'actions',
      render: (_: any, record: any) => {
        let isAmountValid =
          Number(record.amount) > 0 && Number(record.amount) < 100;
        let isEveryDaysValid =
          Number(record.every_days) > 0 && Number(record.every_days) < 30;

        if (!hasPermissions([ERbacPermissions.DYNAMIC_PRICING_RULES_ACTIONS])) {
          return null;
        }

        let actionContent;
        if (!record.isSaved) {
          // Initial state: only Save icon
          actionContent = (
            <Typography.Link
              onClick={() => saveInitial(record.id)}
              style={{ marginRight: 8 }}
            >
              <Tooltip title="Save">
                <i className="da icon-save"></i>
              </Tooltip>
            </Typography.Link>
          );
        } else if (!record.isEditing) {
          // Saved, not editing: show Edit icon
          actionContent = (
            <Typography.Link
              onClick={() => startEditing(record.id)}
              style={{ marginRight: 8 }}
              disabled={isDisableEditing}
            >
              <Tooltip title="Edit">
                <i className="da icon-pencil-o"></i>
              </Tooltip>
            </Typography.Link>
          );
        } else {
          // Editing: show Save and Cancel icons
          actionContent = (
            <>
              <Typography.Link
                onClick={() => saveEdit(record.id)}
                disabled={!isAmountValid || !isEveryDaysValid}
                style={{ marginRight: 8 }}
              >
                <Tooltip title="Save">
                  <i className="da icon-save"></i>
                </Tooltip>
              </Typography.Link>
              <Typography.Link onClick={() => cancelEdit(record.id)}>
                <Tooltip title="Cancel">
                  <i className="da icon-cancel"></i>
                </Tooltip>
              </Typography.Link>
            </>
          );
        }

        return <div className="rulesIcons">{actionContent}</div>;
      },
    },
  ];

  return (
    <LoadingSpinner isLoading={showLoader}>
      <>
        {dataFetchError ? (
          <CustomError {...dataFetchError} />
        ) : (
          <>
            {!hasPermissions([
              ERbacPermissions.DYNAMIC_PRICING_RULES_ACTIONS,
            ]) &&
              !showLoader && (
                <div className="rightFilter dynamicPricingFilter">
                  <FilterDrawer
                    defaultAndValues={{
                      preFilledValues: { ...filterQueryObject },
                    }}
                    onApplyFilter={filterChanged}
                    showBrokerUserDropdown={hasPermissions([
                      ERbacPermissions.FILTER_BROKER_USER_SELECTION_ALL,
                    ])}
                    brokerUserDropdownType="single"
                    showListerDropdown={hasPermissions([
                      ERbacPermissions.FILTER_LISTER_USER_SELECTION_ALL,
                      ERbacPermissions.FILTER_LISTER_USER_SELECTION_TEAM,
                    ])}
                    listerDropdownType="single"
                    showResetFilter={false}
                  />
                </div>
              )}

            {ifShowNoDataMsg ? (
              <div className="mainInnerLayout rulesPageNoData">
                <h3>Dynamic Pricing</h3>
                <p>Lister has not set any Default Pricing Rules yet!</p>
              </div>
            ) : ifShowNoDataMsg && JSON.parse(listers).length === 0 ? (
              <div className="mainInnerLayout rulesPageNoData">
                <h3>Dynamic Pricing</h3>
                <p>You have no listers assigned under this broker!</p>
              </div>
            ) : (
              <Layout
                className="mainInnerLayout rulesPageWrap"
                ref={contentRef}
              >
                <h3>Dynamic Pricing</h3>
                <p>
                  Define account-wide default rules. These apply to all listings
                  unless overridden at the listing level.
                </p>

                <div className="ruleboxMain">
                  <div className="rulesStrategy">
                    <h4>Base Strategy</h4>
                    <p>
                      Applies when the event is more than 60 days away.
                      Configure your minimum markup and safeguard.
                    </p>
                    <div className="rulesMarkup">
                      <h4>Minimum Markup %</h4>
                      <Input
                        type="number"
                        min="10"
                        max="90"
                        style={{ width: 100 }}
                        value={intermediateMinMarkup}
                        disabled={
                          !hasPermissions([
                            ERbacPermissions.DYNAMIC_PRICING_RULES_ACTIONS,
                          ]) || isDisableEditing
                        }
                        onBlur={updateMinMarkup}
                        onChange={(e) =>
                          setIntermediateMinMarkup(Number(e.target.value))
                        }
                      />
                      <small>
                        Floor margin. Adjustments will not lower price below
                        this markup if enabled.
                      </small>
                    </div>
                    <hr />
                  </div>

                  <div className="rulesModifiers">
                    <h4>Time-to-Event Modifiers</h4>
                    <p>
                      Fixed windows; “Within (days)” is locked and Direction is
                      always Decrease.
                    </p>
                    <div className="table-wrapper">
                      <Table
                        columns={columns}
                        dataSource={ruleData}
                        pagination={false}
                      />
                    </div>
                    {hasPermissions([
                      ERbacPermissions.DYNAMIC_PRICING_RULES_ACTIONS,
                    ]) && (
                      <button
                        type="button"
                        className="ant-btn btnStyle2"
                        onClick={applyDefault}
                        disabled={
                          !hasPermissions([
                            ERbacPermissions.DYNAMIC_PRICING_RULES_ACTIONS,
                          ]) || isDisableEditing
                        }
                      >
                        Apply Default
                      </button>
                    )}

                    <hr />
                    <h4>Event Proximity Guidance</h4>
                    <ul>
                      <li>
                        61-90 days away: Standard interval adjustments only.
                      </li>
                      <li>
                        31-60 days away: Increase adjustment frequency (e.g.,
                        every 2 days).
                      </li>
                      <li>
                        {delistingThresholdVal}-30 days away: Aggressive price
                        drops (daily).
                      </li>
                    </ul>
                  </div>
                  {hasPermissions([
                    ERbacPermissions.DYNAMIC_PRICING_RULES_ACTIONS,
                  ]) && (
                    <div
                      className={`floatingFooter ${
                        isFloating ? 'non-floating' : 'floating'
                      }`}
                    >
                      <button
                        type="button"
                        className="ant-btn linkBtn"
                        onClick={resetData}
                        disabled={
                          !hasPermissions([
                            ERbacPermissions.DYNAMIC_PRICING_RULES_ACTIONS,
                          ]) ||
                          !(isSaveDisabledMarkup || isSaveDisabledRules) ||
                          isDisableReset
                        }
                      >
                        Reset
                      </button>
                      <button
                        type="button"
                        className="ant-btn btnStyle2"
                        onClick={initiateSave}
                        disabled={
                          !hasPermissions([
                            ERbacPermissions.DYNAMIC_PRICING_RULES_ACTIONS,
                          ]) || !(isSaveDisabledMarkup || isSaveDisabledRules)
                        }
                      >
                        SAVE
                      </button>
                    </div>
                  )}
                </div>
              </Layout>
            )}
          </>
        )}
      </>
    </LoadingSpinner>
  );
};

export default Rules;
